import React, { useEffect, useRef, useState } from 'react'
import { X, Undo2, Trash2, Save, Loader2, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize } from 'lucide-react'
import { assignmentApi, type Submission } from '../api/lessonBuilderApi'

// CHẤM BÀI: GV vẽ/khoanh/viết điểm lên file HS nộp (ảnh hoặc PDF) → xuất PNG → lưu & gửi cho HS.
// Tải file về BLOB rồi vẽ (blob same-origin → canvas KHÔNG bị taint → xuất PNG được, kể cả file cross-origin).
const isPdf = (u: string) => /\.pdf($|\?)/i.test(u)
const COLORS = ['#dc2626', '#2563eb', '#16a34a', '#1f2937']
type Pt = { x: number; y: number }
type Stroke = { color: string; width: number; pts: Pt[] }

const AnnotateModal: React.FC<{
  fileUrl: string; submissionId: string; onClose: () => void; onSaved: (s: Submission) => void
}> = ({ fileUrl, submissionId, onClose, onSaved }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const baseRef = useRef<HTMLCanvasElement | null>(null)   // nền (ảnh hoặc trang PDF) đã render
  const strokesRef = useRef<Stroke[]>([])
  const drawingRef = useRef<Stroke | null>(null)
  const pdfRef = useRef<any>(null)
  const objUrlRef = useRef<string | null>(null)            // blob URL của ảnh (để thu hồi)
  const [color, setColor] = useState(COLORS[0])
  const [width, setWidth] = useState(4)
  const [zoom, setZoom] = useState(1)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')
  const [page, setPage] = useState(1)
  const [numPages, setNumPages] = useState(1)

  const redraw = () => {
    const cv = canvasRef.current, base = baseRef.current
    if (!cv || !base) return
    const ctx = cv.getContext('2d'); if (!ctx) return
    ctx.clearRect(0, 0, cv.width, cv.height)
    ctx.drawImage(base, 0, 0)
    const all = drawingRef.current ? [...strokesRef.current, drawingRef.current] : strokesRef.current
    for (const s of all) {
      if (!s.pts.length) continue
      ctx.strokeStyle = s.color; ctx.lineWidth = s.width; ctx.lineCap = 'round'; ctx.lineJoin = 'round'
      ctx.beginPath(); ctx.moveTo(s.pts[0].x, s.pts[0].y)
      for (let i = 1; i < s.pts.length; i++) ctx.lineTo(s.pts[i].x, s.pts[i].y)
      if (s.pts.length === 1) ctx.lineTo(s.pts[0].x + 0.1, s.pts[0].y + 0.1)
      ctx.stroke()
    }
  }

  const loadImageBlob = (blobUrl: string) => new Promise<void>((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const maxW = 1600
      const sc = img.naturalWidth > maxW ? maxW / img.naturalWidth : 1
      const w = Math.max(1, Math.round(img.naturalWidth * sc)), h = Math.max(1, Math.round(img.naturalHeight * sc))
      const base = document.createElement('canvas'); base.width = w; base.height = h
      base.getContext('2d')!.drawImage(img, 0, 0, w, h)
      baseRef.current = base
      const cv = canvasRef.current!; cv.width = w; cv.height = h
      resolve()
    }
    img.onerror = () => reject(new Error('Không tải được ảnh'))
    img.src = blobUrl   // blob: → same-origin → không taint
  })

  const renderPdfPage = async (p: number) => {
    const pdf = pdfRef.current; if (!pdf) return
    const pg = await pdf.getPage(p)
    const v1 = pg.getViewport({ scale: 1 })
    const scale = Math.min(1400 / v1.width, 2)
    const viewport = pg.getViewport({ scale })
    const base = document.createElement('canvas')
    base.width = Math.round(viewport.width); base.height = Math.round(viewport.height)
    await pg.render({ canvasContext: base.getContext('2d')!, viewport }).promise
    baseRef.current = base
    const cv = canvasRef.current!; cv.width = base.width; cv.height = base.height
    strokesRef.current = []
    redraw()
  }

  useEffect(() => {
    let alive = true
    ;(async () => {
      setLoading(true); setMsg('')
      try {
        const resp = await fetch(fileUrl)
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
        const blob = await resp.blob()
        if (!alive) return
        if (isPdf(fileUrl) || blob.type === 'application/pdf') {
          const pdfjsLib: any = await import('pdfjs-dist')
          pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`
          const buf = await blob.arrayBuffer()
          const pdf = await pdfjsLib.getDocument({ data: buf }).promise
          if (!alive) return
          pdfRef.current = pdf; setNumPages(pdf.numPages); setPage(1)
          await renderPdfPage(1)
        } else {
          const u = URL.createObjectURL(blob); objUrlRef.current = u
          await loadImageBlob(u)
          strokesRef.current = []; redraw()
        }
      } catch (e) {
        setMsg('⚠️ Không tải được file để chấm (' + ((e as Error)?.message || 'lỗi mạng') + ').')
      } finally { if (alive) setLoading(false) }
    })()
    return () => { alive = false; if (objUrlRef.current) { URL.revokeObjectURL(objUrlRef.current); objUrlRef.current = null } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUrl])

  const goPage = async (p: number) => {
    if (p < 1 || p > numPages || !pdfRef.current || loading) return
    setLoading(true); setPage(p); setMsg('')
    try { await renderPdfPage(p) } finally { setLoading(false) }
  }

  const toPt = (e: React.PointerEvent): Pt => {
    const cv = canvasRef.current!; const r = cv.getBoundingClientRect()
    return { x: (e.clientX - r.left) * (cv.width / r.width), y: (e.clientY - r.top) * (cv.height / r.height) }
  }
  const onDown = (e: React.PointerEvent) => {
    if (loading) return
    ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
    drawingRef.current = { color, width, pts: [toPt(e)] }; redraw()
  }
  const onMove = (e: React.PointerEvent) => {
    if (!drawingRef.current) return
    drawingRef.current.pts.push(toPt(e)); redraw()
  }
  const onUp = () => {
    if (drawingRef.current) { strokesRef.current = [...strokesRef.current, drawingRef.current]; drawingRef.current = null; redraw() }
  }
  const undo = () => { strokesRef.current = strokesRef.current.slice(0, -1); redraw() }
  const clearAll = () => { strokesRef.current = []; redraw() }

  // Lưu: xuất PNG → gửi cho HS + lưu hệ thống → TỰ THOÁT chế độ chấm.
  const save = () => {
    const cv = canvasRef.current; if (!cv) return
    setSaving(true); setMsg('')
    try {
      cv.toBlob(async (blob) => {
        if (!blob) { setMsg('Không xuất được ảnh ghi chú.'); setSaving(false); return }
        try {
          const file = new File([blob], `cham-bai-trang-${page}.png`, { type: 'image/png' })
          const s = await assignmentApi.uploadAnnotation(submissionId, file)
          onSaved(s)
          onClose()   // tự thoát chế độ chấm sau khi lưu & gửi
        } catch (e) {
          setMsg('Lưu thất bại: ' + ((e as Error)?.message || ''))
          setSaving(false)
        }
      }, 'image/png')
    } catch {
      setMsg('Trình duyệt chặn xuất ảnh. Không lưu được.'); setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[300] bg-black/60 flex flex-col" onClick={onClose}>
      <div className="bg-white w-full max-w-5xl mx-auto my-3 rounded-2xl shadow-2xl flex flex-col max-h-[94vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Thanh công cụ */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 flex-wrap">
          <span className="font-black text-slate-800 text-sm mr-1">🖊️ Chấm bài</span>
          {COLORS.map((c) => (
            <button key={c} onClick={() => setColor(c)} title="Màu bút"
              className={`w-6 h-6 rounded-full border-2 ${color === c ? 'border-slate-800 scale-110' : 'border-white'} shadow`}
              style={{ background: c }} />
          ))}
          <select value={width} onChange={(e) => setWidth(Number(e.target.value))}
            className="ml-1 text-sm border border-gray-200 rounded-lg px-2 py-1 outline-none">
            <option value={2}>Mảnh</option><option value={4}>Vừa</option><option value={7}>Đậm</option><option value={12}>Rất đậm</option>
          </select>
          <button onClick={undo} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gray-50 text-gray-700 text-sm font-semibold hover:bg-gray-100"><Undo2 size={15} /> Hoàn tác</button>
          <button onClick={clearAll} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-rose-50 text-rose-600 text-sm font-semibold hover:bg-rose-100"><Trash2 size={15} /> Xoá nét</button>

          {/* Zoom */}
          <span className="inline-flex items-center gap-1 ml-1 border border-gray-200 rounded-lg px-1">
            <button onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))} title="Thu nhỏ" className="p-1 rounded hover:bg-gray-100"><ZoomOut size={16} /></button>
            <span className="w-11 text-center text-xs font-bold text-slate-600 tabular-nums">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))} title="Phóng to" className="p-1 rounded hover:bg-gray-100"><ZoomIn size={16} /></button>
            <button onClick={() => setZoom(1)} title="Vừa khít" className="p-1 rounded hover:bg-gray-100"><Maximize size={15} /></button>
          </span>

          {numPages > 1 && (
            <span className="inline-flex items-center gap-1 ml-1 text-sm text-slate-600">
              <button onClick={() => goPage(page - 1)} disabled={page <= 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronLeft size={16} /></button>
              Trang {page}/{numPages}
              <button onClick={() => goPage(page + 1)} disabled={page >= numPages} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronRight size={16} /></button>
            </span>
          )}
          <button onClick={save} disabled={saving || loading}
            className="ml-auto inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Lưu &amp; gửi
          </button>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
        </div>
        {msg && <div className="px-4 py-1.5 text-sm text-slate-600 border-b border-gray-50">{msg}</div>}
        {/* Vùng vẽ — cuộn được, zoom bằng CSS width (toPt tự quy đổi theo tỉ lệ) */}
        <div className="flex-1 overflow-auto bg-gray-100 p-3 text-center">
          {loading && <div className="text-gray-400 py-16"><Loader2 className="inline animate-spin mr-2" /> Đang tải…</div>}
          <canvas
            ref={canvasRef}
            onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
            className={`bg-white shadow rounded inline-block align-top ${loading ? 'hidden' : ''}`}
            style={{ width: `${zoom * 100}%`, maxWidth: 'none', height: 'auto', touchAction: 'none', cursor: 'crosshair' }}
          />
        </div>
        <div className="px-4 py-2 text-[11px] text-gray-400 border-t border-gray-50">
          Mẹo: phóng to để chấm chi tiết; vẽ/khoanh/viết điểm trực tiếp lên bài. Bấm <b>Lưu &amp; gửi</b> để gửi bản đã chấm cho học sinh (tự thoát).
        </div>
      </div>
    </div>
  )
}

export default AnnotateModal
